import IosShareIcon from '@mui/icons-material/IosShare';
import { Box, Button, Chip, Divider, Stack, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';

import { MessageKey, useI18n } from '../../i18n';
import { Reading } from '../draw';
import { playCue } from '../feedback';
import {
    closingPromptKeys,
    interpretReading,
    overallOf,
    PositionedCard,
    PositionReading,
    promptKeyOf,
    summaryOf,
} from '../interpret';
import { copyLink, shareCard } from '../shareCard';
import { SPREADS } from '../spreads';

import { TarotCard } from './TarotCard';

interface Props {
    reading: Reading;
    /** Turn the cards over one by one on arrival (a fresh reading) instead of showing them turned (history). */
    autoTurn?: boolean;
}

const VERDICT_KEY: Record<string, MessageKey> = {
    yes: 'reading.verdict.yes',
    no: 'reading.verdict.no',
    maybe: 'reading.verdict.maybe',
};

const Entry: React.FC<{
    card: PositionedCard;
    reading: Reading;
    turned: boolean;
    single: boolean;
    onTurn: () => void;
}> = ({ card, reading, turned, single, onTurn }) => {
    const { t } = useI18n();
    const label = t(`pos.${card.position}` as MessageKey, {
        name: reading.name ?? '',
    });
    const isYesNo = reading.spread === 'yesno';
    // The hand-written long text: the card of the day, or the card read through the spread's lens for its position.
    const isDaily = reading.spread === 'daily';
    const longText:
        (Omit<PositionReading, 'lead'> & { lead?: string }) | undefined =
        isDaily
            ? card.daily && {
                  title: card.daily.title,
                  paragraphs: card.daily.body,
                  advice: card.daily.advice,
              }
            : card.positioned;

    const text = (
        <Box
            sx={{
                opacity: turned ? 1 : 0,
                transform: turned ? 'none' : 'translateY(10px)',
                transition: 'opacity .8s .5s, transform .8s .5s',
                minWidth: 0,
                textAlign: single ? 'center' : 'left',
            }}
        >
            <Typography
                variant="overline"
                color="primary"
                sx={{ letterSpacing: '0.25em' }}
            >
                {label}
            </Typography>
            <Typography variant="h6" component="h3" sx={{ lineHeight: 1.25 }}>
                {card.name}
            </Typography>
            <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent={single ? 'center' : 'flex-start'}
                sx={{ my: 0.75, flexWrap: 'wrap', rowGap: 0.5 }}
            >
                <Chip
                    size="small"
                    variant="outlined"
                    label={
                        card.drawn.reversed
                            ? t('reading.reversed')
                            : t('reading.upright')
                    }
                    color={card.drawn.reversed ? 'secondary' : 'primary'}
                />
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontStyle: 'italic' }}
                >
                    {card.keywords}
                </Typography>
            </Stack>
            {isYesNo && (
                <Typography
                    variant="h3"
                    component="p"
                    color="primary"
                    sx={{ my: 1, textShadow: '0 0 24px rgba(212,175,106,.6)' }}
                >
                    {t(VERDICT_KEY[card.verdict])}
                </Typography>
            )}
            {longText && (
                <Box sx={{ mt: 1.5, textAlign: 'left' }}>
                    {longText.lead && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.75, fontStyle: 'italic' }}
                        >
                            {longText.lead}
                        </Typography>
                    )}
                    <Typography
                        variant="h6"
                        component="h4"
                        color="primary"
                        sx={{ mb: 1, textAlign: single ? 'center' : 'left' }}
                    >
                        {longText.title}
                    </Typography>
                    {longText.paragraphs.map((paragraph, i) => (
                        <Typography
                            key={i}
                            variant="body1"
                            sx={{ mt: i ? 1.25 : 0 }}
                        >
                            {paragraph}
                        </Typography>
                    ))}
                    {longText.advice && (
                        <Box
                            sx={{
                                mt: 2,
                                pl: 1.5,
                                borderLeft: '2px solid rgba(212,175,106,.6)',
                            }}
                        >
                            <Typography
                                variant="overline"
                                color="primary"
                                sx={{ letterSpacing: '.2em' }}
                            >
                                {t('reading.dailyAdvice')}
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{ fontStyle: 'italic' }}
                            >
                                {longText.advice}
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}
            {card.note && (
                <Box sx={{ mt: 1.5, textAlign: 'left' }}>
                    <Typography variant="body1">{card.note.text}</Typography>
                    <Box
                        sx={{
                            mt: 2,
                            pl: 1.5,
                            borderLeft: '2px solid rgba(212,175,106,.6)',
                        }}
                    >
                        <Typography
                            variant="overline"
                            color="primary"
                            sx={{ letterSpacing: '.2em' }}
                        >
                            {t('reading.ask')}
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{ fontStyle: 'italic' }}
                        >
                            {card.note.ask}
                        </Typography>
                    </Box>
                </Box>
            )}
            {!longText && !card.note && card.shows !== 'advice' && (
                <Typography variant="body1">{card.meaning}</Typography>
            )}
            {!longText && !card.note && card.shows !== 'meaning' && (
                <Typography
                    variant="body1"
                    sx={{ mt: card.shows === 'both' ? 1.25 : 0 }}
                >
                    {card.shows === 'both' && (
                        <Box
                            component="span"
                            sx={{ color: 'primary.main', fontWeight: 600 }}
                        >
                            {t('reading.advice')}:{' '}
                        </Box>
                    )}
                    {card.advice}
                </Typography>
            )}
        </Box>
    );

    return (
        <Stack
            direction={single ? 'column' : 'row'}
            spacing={single ? 2.5 : 2}
            alignItems={single ? 'center' : 'flex-start'}
        >
            <TarotCard
                cardId={card.drawn.id}
                name={card.name}
                reversed={card.drawn.reversed}
                faceUp={turned}
                width={single ? 190 : 104}
                glow={!turned}
                onClick={turned ? undefined : onTurn}
                label={
                    turned ? card.name : `${label}: ${t('reading.tapToTurn')}`
                }
            />
            {text}
        </Stack>
    );
};

/** A reading: the cards turn over, and under each one its text for that position. */
export const ReadingView: React.FC<Props> = ({ reading, autoTurn }) => {
    const { t, language } = useI18n();
    const cards = useMemo(
        () => interpretReading(reading, language),
        [reading, language]
    );
    const [turned, setTurned] = useState<boolean[]>(() =>
        cards.map(() => !autoTurn)
    );
    const spread = SPREADS[reading.spread];
    const single = cards.length === 1;

    const [shareNote, setShareNote] = useState('');
    const [sharing, setSharing] = useState(false);
    const first = cards[0];

    const share = async () => {
        setSharing(true);
        setShareNote('');
        // Before any await: browsers only allow the copy right after the tap.
        const copied = await copyLink();
        try {
            const result = await shareCard({
                cardId: first.drawn.id,
                name: first.name,
                reversed: first.drawn.reversed,
                adviceLabel: t('reading.dailyAdvice'),
                advice: first.daily?.advice ?? first.advice,
                appName: t('app.name'),
                message: t('reading.shareMessage'),
            });
            if (copied && result !== 'cancelled')
                setShareNote(t('reading.shareLinkCopied'));
            else if (result === 'downloaded')
                setShareNote(t('reading.shareDownloaded'));
        } catch {
            setShareNote(t('reading.shareFailed'));
        } finally {
            setSharing(false);
        }
    };

    const turn = (i: number) =>
        setTurned((prev) => {
            if (prev[i]) return prev;
            playCue('flip');
            return prev.map((v, j) => (j === i ? true : v));
        });

    useEffect(() => {
        if (!autoTurn) return;
        const timers = cards.map((_, i) =>
            window.setTimeout(
                () => {
                    playCue(i === cards.length - 1 ? 'reveal' : 'flip');
                    setTurned((prev) =>
                        prev.map((v, j) => (j === i ? true : v))
                    );
                },
                900 + i * 1400
            )
        );
        return () => timers.forEach(window.clearTimeout);
        // The auto-turn plays once per reading.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reading.id, autoTurn]);

    const allTurned = turned.every(Boolean);
    const overall = overallOf(reading.cards);
    const summary = useMemo(
        () => summaryOf(reading, cards, language),
        [reading, cards, language]
    );
    const promptKeys = overall
        ? closingPromptKeys(reading)
        : [promptKeyOf(reading)];

    return (
        <Stack spacing={3.5} sx={{ pb: 3 }}>
            {reading.name && (
                <Typography
                    variant="h5"
                    component="h2"
                    align="center"
                    color="primary"
                >
                    {t('reading.for', { name: reading.name })}
                </Typography>
            )}
            {!allTurned && (
                <Stack alignItems="center" spacing={1}>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        align="center"
                    >
                        {t('reading.tapToTurn')}
                    </Typography>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => cards.forEach((_, i) => turn(i))}
                    >
                        {t('reading.turnAll')}
                    </Button>
                </Stack>
            )}
            {cards.map((card, i) => (
                <Entry
                    key={card.drawn.id}
                    card={card}
                    reading={reading}
                    turned={turned[i]}
                    single={single}
                    onTurn={() => turn(i)}
                />
            ))}
            {allTurned && (
                <Box
                    sx={{
                        animation: 'arcana-fade 1.2s both',
                        '@keyframes arcana-fade': {
                            from: { opacity: 0 },
                            to: { opacity: 1 },
                        },
                    }}
                >
                    <Divider
                        sx={{ borderColor: 'rgba(212,175,106,.3)', mb: 2.5 }}
                    />
                    {(overall || summary.length > 0) && (
                        <Box sx={{ mb: 2.5 }}>
                            <Typography
                                variant="overline"
                                color="primary"
                                sx={{ letterSpacing: '0.25em' }}
                            >
                                {t('reading.overallTitle')}
                            </Typography>
                            {overall && (
                                <Typography variant="body1">
                                    {t(overall)}
                                </Typography>
                            )}
                            {summary.map((paragraph, i) => (
                                <Typography
                                    key={i}
                                    variant="body1"
                                    sx={{ mt: 1.25 }}
                                >
                                    {paragraph}
                                </Typography>
                            ))}
                        </Box>
                    )}
                    <Box sx={{ mb: 2.5 }}>
                        <Typography
                            variant="overline"
                            color="primary"
                            sx={{ letterSpacing: '0.25em' }}
                        >
                            {t('reading.reflect')}
                        </Typography>
                        {promptKeys.map((key, i) => (
                            <Typography
                                key={key}
                                variant="body1"
                                sx={{ fontStyle: 'italic', mt: i ? 1 : 0 }}
                            >
                                {t(key)}
                            </Typography>
                        ))}
                    </Box>
                    {reading.spread === 'daily' && single && (
                        <Stack alignItems="center" spacing={1} sx={{ mb: 2.5 }}>
                            <Button
                                variant="outlined"
                                startIcon={<IosShareIcon />}
                                disabled={sharing}
                                onClick={share}
                            >
                                {t('reading.share')}
                            </Button>
                            {shareNote && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    role="status"
                                >
                                    {shareNote}
                                </Typography>
                            )}
                        </Stack>
                    )}
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        component="p"
                    >
                        {autoTurn && spread && `${t('reading.saved')}. `}
                        {t('reading.disclaimer')}
                    </Typography>
                </Box>
            )}
        </Stack>
    );
};
